import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { subscribeSubprocessors } from '@/legal/api/subprocessorsApi';

export function SubprocessorSubscribeForm() {
  const { t } = useTranslation('legal');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t('subscribe.errors.privacy_consent_required'));
      return;
    }
    setSubmitting(true);
    try {
      await subscribeSubprocessors(email, consent);
      toast.success(t('subscribe.success'));
      setEmail('');
      setConsent(false);
    } catch (err: unknown) {
      const errKey = err instanceof Error ? err.message : 'generic';
      const knownKeys = ['invalid_email', 'privacy_consent_required', 'rate_limited'];
      const useKey = knownKeys.includes(errKey) ? errKey : 'generic';
      toast.error(t(`subscribe.errors.${useKey}`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="my-12 rounded-xl border border-border bg-surface-subtle p-6">
      <h2 className="text-xl font-semibold text-text-primary">{t('subscribe.title')}</h2>
      <p className="mt-2 text-sm text-text-secondary">{t('subscribe.description')}</p>
      <form onSubmit={handleSubmit} className="mt-4 space-y-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('subscribe.emailPlaceholder')}
          className="h-11 w-full rounded-lg border border-border bg-surface-raised px-3 text-sm text-text-primary placeholder:text-text-secondary focus:border-border-focus focus:outline-none"
        />
        <label className="flex items-start gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1"
          />
          <span>{t('subscribe.consentLabel')}</span>
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="h-11 rounded-lg bg-action-primary-bg px-5 text-sm font-medium text-text-on-brand transition hover:bg-action-primary-bg-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? t('subscribe.submitting') : t('subscribe.submit')}
        </button>
      </form>
    </section>
  );
}
